import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { RegisterData } from '../../types/user';
import { validateForm, ValidationRules, ValidationPatterns } from '../../../../shared/utils';
import Button from '../../../../shared/components/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faIdCard, faEnvelope, faLock, faEye, faEyeSlash, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { useToast } from '../../../../shared/contexts/ToastContext';
import { TurnstileWidget } from '../../../../shared/components';
import { useTurnstile } from '../../../../shared/hooks';
import { useAvailabilityCheck } from '../../hooks/useAvailabilityCheck';
import { AvailabilityIndicator } from './AvailabilityIndicator';
import { useRegistration } from '../../contexts/RegistrationContext';

interface RegisterFormData extends RegisterData {
  phone: string;
}

/**
 * RegisterForm component with validation
 * Requirements: 1.1
 */
export const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { showSuccess, showError } = useToast();
  const { setJustRegistered, setRegistrationData, isEmailFirstFlowEnabled } = useRegistration();
  
  const [formData, setFormData] = useState<RegisterFormData>({
    username: '',
    email: '',
    password: '',
    fullName: '',
    phone: '',
  });
  
  const [errors, setErrors] = useState<Partial<Record<keyof RegisterFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registerError, setRegisterError] = useState<string>('');
  
  // Turnstile verification
  const { token, setToken, verifyToken, isVerifying, reset: resetTurnstile } = useTurnstile();
  const isTurnstileEnabled = !!import.meta.env.VITE_TURNSTILE_SITE_KEY;

  // Real-time availability checking
  const { availability, validateUsername, validateEmail, clearAvailability } = useAvailabilityCheck();

  const validationRules: ValidationRules = {
    username: {
      required: true,
      minLength: 3,
      pattern: ValidationPatterns.username,
    },
    email: {
      required: true,
      pattern: ValidationPatterns.email,
    },
    fullName: {
      required: true,
      minLength: 2,
    },
    phone: {
      required: true,
      pattern: /^(\+?62|0)8[0-9]{8,12}$/,
    },
    password: {
      required: true,
      minLength: 6,
    },
  };

  const validateFormData = (): boolean => {
    const newErrors = validateForm(formData, validationRules, {
      username: 'Username',
      email: 'Email',
      fullName: 'Full name',
      phone: 'Phone number',
      password: 'Password',
    });

    // Add availability validation
    if (availability.username === 'taken') {
      newErrors.username = 'Username sudah digunakan. Silakan pilih username lain.';
    }
    
    if (availability.email === 'taken') {
      newErrors.email = 'Email sudah terdaftar. Silakan gunakan email lain atau login.';
    }

    // Prevent submission if still checking availability
    if (availability.username === 'checking' || availability.email === 'checking') {
      // Don't set error, just prevent submission
      return false;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[name as keyof RegisterFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }

    // Trigger real-time availability check for username and email
    if (name === 'username' && value.length >= 3) {
      validateUsername(value);
    } else if (name === 'username' && value.length < 3) {
      clearAvailability('username');
    }

    if (name === 'email' && value.includes('@')) {
      validateEmail(value);
    } else if (name === 'email' && !value.includes('@')) {
      clearAvailability('email');
    }
  };



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateFormData()) {
      return;
    }

    // Verify Turnstile if enabled
    if (isTurnstileEnabled) {
      if (!token) {
        setRegisterError('Silakan selesaikan verifikasi keamanan terlebih dahulu.');
        return;
      }

      setIsSubmitting(true);
      const isVerified = await verifyToken();
      
      if (!isVerified) {
        setRegisterError('Verifikasi keamanan gagal. Silakan refresh halaman dan coba lagi.');
        setIsSubmitting(false);
        resetTurnstile();
        return;
      }
    }

    setIsSubmitting(true);
    setRegisterError('');

    try {
      // Normalize phone number
      let normalizedPhone = formData.phone;
      if (formData.phone.startsWith('0')) {
        normalizedPhone = '+62' + formData.phone.substring(1);
      } else if (!formData.phone.startsWith('+')) {
        normalizedPhone = '+' + formData.phone;
      }

      // FIXED: Register user FIRST, then wait for auth state to settle
      await register({
        ...formData,
        phone: normalizedPhone
      });
      
      // Set registration state for enhanced onboarding
      setJustRegistered(true);
      setRegistrationData({ username: formData.username });
      
      // Show success notification
      showSuccess('Pendaftaran berhasil! Selamat datang 🎉');
      
      // CRITICAL FIX: Wait for auth state to fully settle before navigation
      // This prevents race conditions between registration and navigation
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Navigate to check-email AFTER auth state settled
      if (isEmailFirstFlowEnabled) {
        navigate('/check-email', { 
          state: { 
            email: formData.email,
            fromRegistration: true
          },
          replace: true 
        });
      } else {
        // EXISTING FLOW: Navigate to dashboard if email-first flow is disabled
        navigate('/dashboard', { replace: true });
      }
    } catch (error: any) {
      const errorMessage = error?.message || 'Terjadi kesalahan saat mendaftar. Silakan coba lagi.';
      console.error('Registration error:', error);
      
      // Show toast for important duplicate errors (better UX)
      const isDuplicateError = 
        errorMessage.includes('sudah digunakan') || 
        errorMessage.includes('sudah terdaftar') ||
        errorMessage.includes('sudah digunakan');
      
      if (isDuplicateError) {
        showError(errorMessage);
        // Also show in form for consistency
        setRegisterError(errorMessage);
      } else {
        // For other errors, only show in form to avoid double notifications
        setRegisterError(errorMessage);
      }
      
      // No session storage cleanup needed since we don't use it anymore
      
      // Only reset Turnstile for server errors, not validation errors
      // This prevents frustrating UX where users have to solve captcha again for simple validation issues
      const isServerError = 
        error?.status >= 500 || 
        errorMessage.includes('server') ||
        errorMessage.includes('network') ||
        errorMessage.includes('timeout') ||
        errorMessage.includes('Gagal membuat profil') ||
        errorMessage.includes('Pendaftaran gagal');
      
      if (isTurnstileEnabled && isServerError) {
        console.log('🔄 Resetting Turnstile due to server error');
        resetTurnstile();
      } else if (isTurnstileEnabled) {
        console.log('ℹ️ Keeping Turnstile token - validation error only');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      <div className="bg-white rounded-3xl shadow-lg p-6 md:p-8">
        {/* Header */}
        <div className="mb-5">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">
            Daftar akun baru
          </h2>
          <p className="text-xs md:text-sm text-gray-600">
            Buat akun untuk mulai menggunakan layanan kami
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-3.5">
          {/* Username Field */}
          <div>
            <label
              className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5"
              htmlFor="username"
            >
              Username
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <FontAwesomeIcon icon={faUser} className="w-4 h-4 md:w-5 md:h-5" />
              </span>
              <input
                id="username"
                name="username"
                type="text"
                value={formData.username}
                onChange={handleChange}
                className={`w-full pl-9 md:pl-10 ${availability.username ? 'pr-10 md:pr-11' : 'pr-3 md:pr-4'} py-2 md:py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                  errors.username ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Masukan Username Anda"
                disabled={isSubmitting}
              />
              <AvailabilityIndicator 
                status={availability.username} 
                field="username" 
                value={formData.username} 
              />
            </div>
            {errors.username && (
              <p className="text-red-500 text-xs mt-1">{errors.username}</p>
            )}

          </div>

          {/* Full Name Field */}
          <div>
            <label
              className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5"
              htmlFor="fullName"
            >
              Nama Lengkap
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <FontAwesomeIcon icon={faIdCard} className="w-4 h-4 md:w-5 md:h-5" />
              </span>
              <input
                id="fullName"
                name="fullName"
                type="text"
                value={formData.fullName}
                onChange={handleChange}
                className={`w-full pl-9 md:pl-10 pr-3 md:pr-4 py-2 md:py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                  errors.fullName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="John Doe"
                disabled={isSubmitting}
              />
            </div>
            {errors.fullName && (
              <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>
            )}
          </div>

          {/* Email Field */}
          <div>
            <label
              className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5"
              htmlFor="email"
            >
              Email
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <FontAwesomeIcon icon={faEnvelope} className="w-4 h-4 md:w-5 md:h-5" />
              </span>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full pl-9 md:pl-10 ${availability.email ? 'pr-10 md:pr-11' : 'pr-3 md:pr-4'} py-2 md:py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="john@example.com"
                disabled={isSubmitting}
              />
              <AvailabilityIndicator 
                status={availability.email} 
                field="email" 
                value={formData.email} 
              />
            </div>
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          {/* WhatsApp Field */}
          <div>
            <label
              className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5"
              htmlFor="phone"
            >
              WhatsApp
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <FontAwesomeIcon icon={faWhatsapp} className="w-4 h-4 md:w-5 md:h-5" />
              </span>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full pl-9 md:pl-10 pr-3 md:pr-4 py-2 md:py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="08xxxxxxxxxx"
                disabled={isSubmitting}
              />
            </div>
            {errors.phone ? (
              <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">Format: 08xxx atau +62xxx</p>
            )}
          </div>

          {/* Password Field */}
          <div>
            <label
              className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5"
              htmlFor="password"
            >
              Kata sandi
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <FontAwesomeIcon icon={faLock} className="w-4 h-4 md:w-5 md:h-5" />
              </span>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                className={`w-full pl-9 md:pl-10 pr-10 md:pr-12 py-2 md:py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                  errors.password ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Min. 6 karakter"
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="w-4 h-4 md:w-5 md:h-5" />
              </button>
            </div>
            {errors.password && (
              <p className="text-red-500 text-xs mt-1">{errors.password}</p>
            )}
          </div>

          {/* Error Message */}
          {registerError && (
            <div className="flex items-start gap-2 p-2.5 bg-red-50 border border-red-200 rounded-xl animate-shake">
              <FontAwesomeIcon icon={faCircleExclamation} className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700 flex-1">
                {registerError}
              </p>
            </div>
          )}

          {/* Turnstile Widget */}
          {isTurnstileEnabled && (
            <div className="mb-1">
              <TurnstileWidget
                onSuccess={setToken}
                onError={resetTurnstile}
                onExpire={resetTurnstile}
              />
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting || isVerifying}
            disabled={isTurnstileEnabled && !token}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl font-medium transition-colors mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isVerifying ? 'Memverifikasi...' : 'Daftar'}
          </Button>

          {/* Privacy Policy Notice */}
          <div className="text-center">
            <p className="text-xs text-gray-500 leading-relaxed">
              Dengan mendaftar, Anda menyetujui Ketentuan Layanan & Kebijakan Privasi kami.
            </p>
          </div>

          {/* Login Link */}
          <div className="text-center pt-3 border-t border-gray-200">
            <p className="text-xs md:text-sm text-gray-600">
              Sudah punya akun?{' '}
              <Link
                to="/login"
                className="text-blue-600 hover:text-blue-700 font-semibold"
              >
                Masuk
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};
